import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { ProjectStatus } from '@/modules/projects/types/project';
import { useCustomAttributes } from '@/modules/team/hooks/use-custom-attributes';
import { SearchFilter } from '@/types/filters';

import {
  PLANNER_DATE_RANGE_FILTER_KEY,
  PLANNER_FILTERS_REQUEST_DEBOUNCE_MS,
  PLANNER_PROJECT_STATUS_FILTER_KEY,
} from '../constants';
import { getPlannerFiltersConfig } from '../helpers';
import usePlannerStore from '../store';
import { usePlannerFilterState } from './use-planner-filter-state';

/**
 * Everything the planner page needs to render and drive its filters:
 * the v2 config (with the availability/probability gates), the search filter,
 * the filter state adapter, and the debounced filters that feed the query key.
 */
export const usePlannerPageFilters = () => {
  const { t } = useTranslation('authenticated.planner');
  const filters = usePlannerStore(store => store.filters);
  const filterState = usePlannerFilterState();

  // Custom attributes feed planner filter options. Don't fetch them for users
  // without planner access — the endpoint would 403.
  const { hasPermission } = usePermissions();
  const canViewAllAssignments = hasPermission(OpsiumPolicy.AssignmentsViewAll);
  const { data: customAttributes } = useCustomAttributes({ enabled: canViewAllAssignments });

  // Gates the Availability filter — it only makes sense over an explicit date range
  const hasDateRangeFilter = useMemo(() => {
    const value = filters[PLANNER_DATE_RANGE_FILTER_KEY];
    return !!(Array.isArray(value) ? value[0] : value);
  }, [filters]);

  // Gates the Probability filter — probability exists only on tentative projects
  const hasTentativeProjectStatus = useMemo(() => {
    const value = filters[PLANNER_PROJECT_STATUS_FILTER_KEY];
    const statuses = Array.isArray(value) ? value : value ? [value] : [];
    return statuses.includes(ProjectStatus.Tentative);
  }, [filters]);

  const filtersConfig = useMemo(
    () =>
      getPlannerFiltersConfig(customAttributes, { hasDateRangeFilter, hasTentativeProjectStatus }),
    // `t` keeps the config in sync on language change — getPlannerFiltersConfig reads i18n directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customAttributes, hasDateRangeFilter, hasTentativeProjectStatus, t]
  );

  // Free-text search by member or project name. The key must be `searchString`
  // so mapAssignmentFilters maps it to the assignments request `searchQuery`.
  const searchFilterConfig = useMemo<SearchFilter>(
    () => ({
      type: 'search',
      name: 'searchString',
      label: t('filters.searchLabel'),
      placeholder: t('filters.searchPlaceholder'),
    }),
    [t]
  );
  const searchValue = (filters.searchString as string) ?? '';

  // Throttle filter-driven requests: rapid checkbox toggles (e.g. two member
  // statuses in quick succession) update chips/URL instantly but only the
  // settled filter state reaches the query key — one backend request instead
  // of one per toggle.
  const debouncedFilters = useDebouncedValue(filters, PLANNER_FILTERS_REQUEST_DEBOUNCE_MS);

  return { filters, debouncedFilters, filterState, filtersConfig, searchFilterConfig, searchValue };
};
